// Shared, curve-shape-agnostic math for the interlinked spend / outcome /
// efficiency sliders used by both the Budget Builder (BudgetCard) and the
// Scaling page (ScalingSliderCard).
//
// Each side fits its own saturation curve with its own point shape — a Budget
// Builder CurvePoint carries its modeled outcome as `volume`, a Scaling
// ScalingCurvePoint as `fit` — so these helpers take accessor functions (or raw
// numbers) rather than a concrete point type. That keeps a single implementation
// of the range / snapping / efficiency math behind both cards.

export type EfficiencyMetric = 'CPA' | 'ROAS';

export const safeRatio = (num: number | null, den: number | null): number | null =>
  num != null && den != null && den > 0 ? num / den : null;

export const clampIndex = (n: number, max: number): number => Math.max(0, Math.min(n, max));

/** [min, max] of a numeric accessor over a curve, ignoring nulls (falls back to [0, 1]). */
export const rangeOf = <T>(items: T[], get: (x: T) => number | null): [number, number] => {
  const vals = items.map(get).filter((v): v is number => v != null);
  return vals.length ? [Math.min(...vals), Math.max(...vals)] : [0, 1];
};

/**
 * Index of the point whose accessor value is nearest `target` (nulls skipped).
 * The linked sliders only select fitted points, so a raw drag value snaps to the
 * nearest one along whichever metric that slider is positioned by.
 */
export const nearestIndexBy = <T>(
  items: T[],
  get: (x: T) => number | null,
  target: number
): number => {
  let best = 0;
  let bestDelta = Infinity;
  items.forEach((p, i) => {
    const v = get(p);
    if (v != null && Math.abs(v - target) < bestDelta) {
      bestDelta = Math.abs(v - target);
      best = i;
    }
  });
  return best;
};

export interface DerivedEfficiency {
  value: number | null;
  lower: number | null;
  upper: number | null;
}

/**
 * Efficiency derived from spend & modeled outcome — the single source of truth so
 * it's correct regardless of any stored efficiency columns. ROAS = value/spend
 * (bands in the same direction); CPA = spend/value (bands invert — a lower CPA
 * comes from the higher outcome). Point-agnostic: callers pass the outcome value
 * and its CI bounds.
 */
export const derivedEfficiency = (
  metric: EfficiencyMetric,
  spend: number,
  value: number | null,
  lower: number | null,
  upper: number | null
): DerivedEfficiency =>
  metric === 'ROAS'
    ? {
        value: safeRatio(value, spend),
        lower: safeRatio(lower, spend),
        upper: safeRatio(upper, spend)
      }
    : {
        value: safeRatio(spend, value),
        lower: safeRatio(spend, upper),
        upper: safeRatio(spend, lower)
      };
